import setupScene, { SceneResources, SceneSetupOptions } from './setupScene';
import createRenderLoop from './renderLoop';
import animations, { AnimationContext } from './animations';
import loadCharacter from './loadCharacter';
import cloudLoader, { loadCloud } from './loadCloud';
import breathRingLoader, {
  getRingPosition,
  setRingPosition,
  moveRingBy,
  enableRingDebug,
} from './loadBreathRing';
import gameLogic from './gameLogic';
import { Vector3 } from 'three';
import {
  AxesHelper,
  Box3,
  BoxHelper,
  Object3D,
  Mesh,
  PlaneGeometry,
  MeshBasicMaterial,
  DoubleSide,
  AdditiveBlending,
  CanvasTexture,
  Group,
} from 'three';

let resources: SceneResources | null = null;
let loopHandle: ReturnType<typeof createRenderLoop> | null = null;
let characterHandle: ReturnType<typeof loadCharacter> | null = null;
let cloudHandle: { group: Group; dispose: () => void } | null = null;
let auraMesh: Mesh | null = null;
let breathRingMesh: Mesh | null = null;
let buddhaGroup: Group | null = null; // Parent group for Buddha + breath ring
let axesHelper: AxesHelper | null = null;
let boxHelper: BoxHelper | null = null;
let debugEnabled = false;

// Animation context passed to animation functions each frame
let animationContext: AnimationContext = {};

// Test sequence state for movement testing
let testSequenceActive = false;
let testSequenceTimeoutIds: number[] = [];

// Game state tracking
let gameStarted = false;
// previously used for cloud-win flow; no longer needed
// Session complete when Buddha exits the top of the viewport
let sessionComplete = false;
let sessionFadeProgress = 0; // 0..1 fade for UI/ring
const SESSION_FADE_DURATION = 1.8; // seconds

// Progress UI should reflect the Buddha's actual on-screen rise.
// Store a stable baseline Y (world units) so we can compute a non-jittery
// progress ratio from movement offsets without idle float noise.
let progressBaseRingTopWorldY: number | null = null;

// Audio playback for end-game sequence
let endingAudio: HTMLAudioElement | null = null;
let quoteAudio: HTMLAudioElement | null = null;
const ENDING_AUDIO_URL =
  'https://hsfbmpaclvbltdfaazpo.supabase.co/storage/v1/object/public/audio/game-buddha-ending.mp3';
const QUOTE_AUDIO_BASE_URL =
  'https://hsfbmpaclvbltdfaazpo.supabase.co/storage/v1/object/public/audio/game-buddha-quote-';

function createAudio(src: string) {
  const audio = new Audio(src);
  audio.preload = 'auto';
  audio.crossOrigin = 'anonymous';
  return audio;
}

function stopAndCleanupAudio() {
  try {
    if (endingAudio) {
      try {
        endingAudio.pause();
      } catch (e) {
        void e;
      }
      try {
        endingAudio.src = '';
      } catch (e) {
        void e;
      }
      endingAudio = null;
    }
    if (quoteAudio) {
      try {
        quoteAudio.pause();
      } catch (e) {
        void e;
      }
      try {
        quoteAudio.src = '';
      } catch (e) {
        void e;
      }
      quoteAudio = null;
    }
  } catch (e) {
    void e;
  }
}

function playRandomQuoteAudio() {
  try {
    const idx = Math.floor(Math.random() * 10) + 1; // 1..10
    const url = `${QUOTE_AUDIO_BASE_URL}${idx}.mp3`;
    quoteAudio = createAudio(url);
    // On end, just cleanup the quote audio
    const onEnd = () => {
      try {
        if (quoteAudio) {
          quoteAudio.removeEventListener('ended', onEnd);
          try {
            quoteAudio.src = '';
          } catch (e) {
            void e;
          }
        }
      } catch (e) {
        void e;
      }
      quoteAudio = null;
    };
    quoteAudio.addEventListener('ended', onEnd);
    void quoteAudio.play().catch((err) => {
      // ignore playback errors (autoplay blocked, etc.)
      void err;
    });
  } catch (e) {
    void e;
  }
}

function playEndingThenQuote() {
  try {
    endingAudio = createAudio(ENDING_AUDIO_URL);
    const onEnd = () => {
      try {
        if (endingAudio) {
          endingAudio.removeEventListener('ended', onEnd);
          try {
            endingAudio.src = '';
          } catch (e) {
            void e;
          }
        }
      } catch (e) {
        void e;
      }
      endingAudio = null;
      // Start random quote after ending
      playRandomQuoteAudio();
    };
    endingAudio.addEventListener('ended', onEnd);
    void endingAudio.play().catch((err) => {
      // ignore playback errors; still attempt quote
      void err;
      playRandomQuoteAudio();
    });
  } catch (e) {
    void e;
  }
}

function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(Math.max(value, 0), 1);
}

function getBreathRingTopWorldPosition(): {
  x: number;
  y: number;
  z: number;
} | null {
  if (!buddhaGroup || !breathRingMesh) return null;

  try {
    const ringWorld = new Vector3();
    breathRingMesh.getWorldPosition(ringWorld);

    const box = new Box3().setFromObject(breathRingMesh);
    if (!Number.isFinite(box.max.y)) return null;

    return { x: ringWorld.x, y: box.max.y, z: ringWorld.z };
  } catch (e) {
    void e;
    return null;
  }
}

function getRingViewportProgressPercent(): number {
  if (!resources?.camera || !buddhaGroup) return 0;

  const movement = animations.getMovementState();
  const ringTop = getBreathRingTopWorldPosition();
  if (!ringTop)
    return clamp01(gameLogic.getProgressState()?.progressPercent ?? 0);

  if (progressBaseRingTopWorldY === null) {
    // Capture baseline on first query.
    progressBaseRingTopWorldY = ringTop.y;
  }

  // Derive a stable ring-top Y from movement offsets to avoid idle float wobble.
  // We assume the ring is parented to the same movement offsets as the Buddha group.
  const baseRingTopY = progressBaseRingTopWorldY;
  const currentRingTopY = baseRingTopY + movement.totalOffset;

  // Find the world Y where the ring top would touch the top of the viewport (NDC y == 1)
  // at the ring's current X/Z.
  const a = new Vector3(ringTop.x, baseRingTopY, ringTop.z).project(
    resources.camera
  );
  const b = new Vector3(ringTop.x, baseRingTopY + 1, ringTop.z).project(
    resources.camera
  );
  const slope = b.y - a.y;

  if (!Number.isFinite(slope) || Math.abs(slope) < 1e-6) {
    return clamp01(gameLogic.getProgressState()?.progressPercent ?? 0);
  }

  const ringTopAtViewportTopWorldY = baseRingTopY + (1 - a.y) / slope;
  const denom = ringTopAtViewportTopWorldY - baseRingTopY;
  if (!Number.isFinite(denom) || Math.abs(denom) < 1e-6) return 0;

  return clamp01((currentRingTopY - baseRingTopY) / denom);
}

// ============================================================================
// AURA CREATION
// ============================================================================
// Creates a soft, warm glowing aura behind the Buddha using a canvas texture.
// The aura uses warm sunrise tones (whites, golds, soft orange) and is
// positioned behind the Buddha to create a peaceful, meditative atmosphere.

function createAuraMesh(): Mesh {
  // Create a canvas texture for the soft glow effect
  const canvas = document.createElement('canvas');
  const size = 512;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    // Create a radial gradient for the soft glow
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size / 2;

    // Multi-layered gradient for a more natural glow
    const gradient = ctx.createRadialGradient(
      centerX,
      centerY,
      0, // Inner circle (center)
      centerX,
      centerY,
      radius // Outer circle (edge)
    );

    // Warm sunrise color stops - from bright center to transparent edge
    gradient.addColorStop(0, 'rgba(255, 248, 220, 0.9)'); // Warm white/cream center
    gradient.addColorStop(0.15, 'rgba(255, 236, 179, 0.7)'); // Soft gold
    gradient.addColorStop(0.35, 'rgba(255, 213, 128, 0.4)'); // Warm amber
    gradient.addColorStop(0.55, 'rgba(255, 193, 102, 0.2)'); // Soft orange
    gradient.addColorStop(0.75, 'rgba(255, 178, 102, 0.08)'); // Fading orange
    gradient.addColorStop(1, 'rgba(255, 170, 100, 0)'); // Fully transparent edge

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  }

  const texture = new CanvasTexture(canvas);
  texture.needsUpdate = true;

  // Create the material with transparency and additive blending for a soft glow
  const material = new MeshBasicMaterial({
    map: texture,
    transparent: true,
    opacity: 0.25,
    side: DoubleSide,
    depthWrite: false, // Don't write to depth buffer (allows seeing through)
    blending: AdditiveBlending, // Additive blending for a glowing effect
  });

  // Create a plane geometry for the aura
  // Size should be larger than the Buddha to surround it (scaled down to match smaller Buddha)
  const geometry = new PlaneGeometry(2.0, 2.0);
  const mesh = new Mesh(geometry, material);

  // Position the aura behind and centered on the Buddha
  // The Buddha is now positioned with base around y=-0.55 and is about 1.1 units tall
  // So center the aura around y=0 (roughly center of Buddha) and slightly behind
  // Position the aura slightly behind the Buddha so it creates a halo
  // that doesn't visually intersect the model geometry.
  mesh.position.set(0, 0, -2.6);

  // Render before the Buddha but after the background
  mesh.renderOrder = -500;

  return mesh;
}

export function initScene(container: HTMLElement, options?: SceneSetupOptions) {
  if (!container) return null;
  if (resources) return resources;

  try {
    resources = setupScene(container, options);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('initScene: setupScene failed; WebGL may be blocked', e);
    resources = null;
    return null;
  }

  if (!resources) {
    // eslint-disable-next-line no-console
    console.warn('initScene: resources unavailable, aborting scene init');
    return null;
  }

  // Create a parent group for the Buddha and breath ring
  // This allows them to move together when rising/falling
  buddhaGroup = new Group();
  buddhaGroup.name = 'buddhaGroup';
  resources.scene.add(buddhaGroup);

  // Load the Buddha model and add to the buddha group (not directly to scene)
  try {
    characterHandle = loadCharacter(buddhaGroup);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('initScene: failed to load character', e);
  }

  // Create the 3D breath ring and add to the buddha group
  const shouldShowBreathRing = options?.showBreathRing !== false;
  if (shouldShowBreathRing) {
    try {
      breathRingMesh = breathRingLoader.createBreathRing();
      buddhaGroup.add(breathRingMesh);
      // eslint-disable-next-line no-console
      console.debug('initScene: breath ring created and added to buddhaGroup');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('initScene: failed to create breath ring', e);
    }
  } else {
    // Remove any existing breath ring mesh from the group if present
    if (breathRingMesh && buddhaGroup) {
      try {
        buddhaGroup.remove(breathRingMesh);
      } catch (e) {
        void e;
      }
    }
    breathRingMesh = null;
  }

  // Create and add the aura mesh behind the Buddha (in the buddhaGroup)
  try {
    auraMesh = createAuraMesh();
    buddhaGroup.add(auraMesh);
    // eslint-disable-next-line no-console
    console.debug('initScene: aura mesh created and added to buddhaGroup');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('initScene: failed to create aura mesh', e);
  }

  // Load the cloud (decorative only — goal/heart removed)
  try {
    cloudHandle = loadCloud(resources.scene);
    // decorative cloud only — goal & heart removed
    // eslint-disable-next-line no-console
    console.debug('initScene: cloud loaded (decorative only)');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('initScene: failed to load cloud', e);
  }

  // Set up game callbacks for progress tracking
  gameLogic.setGameCallbacks({
    onCycleComplete: handleCycleComplete,
    onFailure: handleFailure,
  });

  // Start a minimal render loop that will call animation update stubs.
  loopHandle = createRenderLoop(
    resources.renderer,
    resources.scene,
    resources.camera,
    (dt, elapsed) => {
      const gameStatus = gameLogic.getGameStatus();
      const isPaused = gameStatus === 'paused';

      // Update the animation context with current references
      // Use buddhaGroup for movement animations so Buddha + ring move together
      animationContext = {
        characterGroup: buddhaGroup ?? null,
        auraMesh: auraMesh ?? null,
        ringMesh: breathRingMesh ?? null,
        elapsed: elapsed,
      };

      if (isPaused) {
        return;
      }

      // Call animation functions for idle floating and aura pulsing
      animations.updateIdle(animationContext);
      animations.updateAura(animationContext);

      // Call rising/falling animation updates
      // These only do work when the movement mode is active
      animations.updateRise(dt, animationContext);
      animations.updateFall(dt, animationContext);

      // Update breathing system
      gameLogic.updateBreathing(dt);

      // Update the 3D breath ring animation
      const breathState = gameLogic.getBreathingState();
      if (shouldShowBreathRing && breathRingMesh) {
        breathRingLoader.updateBreathRing(
          elapsed,
          dt,
          breathState.currentPhase,
          breathState.phaseProgress,
          breathState.isActive,
          breathState.isFollowingPattern,
          breathState.validWindowEnd,
          breathState.validWindowActive
        );
      }

      // Heart model removed — no heart breathing animation

      // Detect if Buddha has exited the top of the screen (session complete)
      if (!sessionComplete && buddhaGroup && resources && resources.camera) {
        try {
          const ringTop = getBreathRingTopWorldPosition();
          const testPos = ringTop
            ? new Vector3(ringTop.x, ringTop.y, ringTop.z)
            : (() => {
                const pos = new Vector3();
                buddhaGroup!.getWorldPosition(pos);
                return pos;
              })();
          const projected = testPos.clone().project(resources!.camera);
          // Completion when the top of the ring touches the top of the viewport.
          if (projected.y >= 1) {
            sessionComplete = true;
            handleSessionComplete();
          }
        } catch (e) {
          void e;
        }
      }

      // Update cloud floating animation
      cloudLoader.updateCloudFloat(elapsed);

      // If the session just completed, animate a soft fade of UI & ring
      if (sessionComplete) {
        sessionFadeProgress = Math.min(
          1,
          sessionFadeProgress + dt / Math.max(0.01, SESSION_FADE_DURATION)
        );

        // Fade breath ring opacity if present
        if (shouldShowBreathRing && breathRingMesh && breathRingMesh.material) {
          try {
            (breathRingMesh.material as MeshBasicMaterial).opacity = Math.max(
              0,
              1 - sessionFadeProgress
            );
          } catch (e) {
            void e;
          }
        }

        // Reduce aura intensity for soft fade
        animations.setAuraIntensity(1 - sessionFadeProgress * 0.65);
      }

      // Sync progress offset with game progress
      if (
        gameStarted &&
        !animations.isWinAnimationActive() &&
        !sessionComplete
      ) {
        const progressState = gameLogic.getProgressState();
        animations.animateProgressOffset(
          progressState.currentProgress,
          dt,
          1.5
        );

        // Update cloud glow based on proximity (subtle effect)
        const proximity = progressState.progressPercent;
        cloudLoader.setCloudGlowIntensity(proximity * 0.4); // aesthetic only
      }

      // If debug mode is enabled and the character has loaded, ensure
      // a box helper is created and updated each frame so its bounds stay
      // accurate while debugging placement.
      if (debugEnabled && !boxHelper && characterHandle?.group) {
        try {
          boxHelper = new BoxHelper(characterHandle.group, 0xffcc00);
          resources!.scene.add(boxHelper);
        } catch (e) {
          void e;
        }
      }

      if (boxHelper && characterHandle?.group) {
        try {
          boxHelper.setFromObject(characterHandle.group);
        } catch (e) {
          void e;
        }
      }
    }
  );

  loopHandle.start();

  // Set up input listeners for controlling rising/falling movement
  // Pass the container so input only responds within the game area
  gameLogic.setupInputListeners(container);

  // Game starts when user clicks the Start button in the UI
  // startGame() is called from the UI component

  return resources;
}

// ============================================================================
// GAME EVENT HANDLERS
// ============================================================================
// Handle game events from the game logic system.

/**
 * Handle successful breath cycle completion.
 * Awards progress toward the cloud goal.
 */
function handleCycleComplete(): void {
  // eslint-disable-next-line no-console
  console.debug('Scene: breath cycle completed successfully');
  // Progress is already awarded by gameLogic.awardCycleProgress()
  // The render loop will animate the progress offset
}

/**
 * Handle win condition.
 * Triggers the peaceful win animation sequence.
 */
// Scene detects session completion via viewport exit; no legacy handleWin needed

/**
 * Handle breathing failure.
 * Applies a gentle penalty and visual feedback.
 */
function handleFailure(): void {
  // eslint-disable-next-line no-console
  console.debug('Scene: breathing failure, applying penalty');
  // Penalty is already applied by gameLogic.applyFailurePenalty()
  // The render loop will animate the progress offset
}

/**
 * Session complete handler triggered when Buddha exits the top of the camera view.
 */
function handleSessionComplete() {
  // stop game input and breathing
  try {
    gameLogic.markSessionComplete();
  } catch (e) {
    void e;
  }

  // stop movement and input
  try {
    animations.stopMovement();
    gameLogic.cleanupInputListeners?.();
  } catch (e) {
    void e;
  }

  // mark started->false so Scene UI transitions will reflect completion
  gameStarted = false;

  // eslint-disable-next-line no-console
  console.debug('Scene: session complete — Buddha left viewport');

  // Play end-game audio then a random quote if available.
  try {
    stopAndCleanupAudio();
    playEndingThenQuote();
  } catch (e) {
    void e;
  }
}

// ============================================================================
// MOVEMENT CONTROL EXPORTS
// ============================================================================
// These functions are re-exported from animations.ts for external use.
// They will be connected to input (mouse/touch) in later steps.

export function startRising() {
  animations.startRising();
}

export function startFalling() {
  animations.startFalling();
}

export function stopMovement() {
  animations.stopMovement();
}

export function resetMovement() {
  animations.resetMovement();
}

// ============================================================================
// GAME CONTROL FUNCTIONS
// ============================================================================
// Functions to control the game state.

/**
 * Start the breathing game.
 * The Buddha will begin responding to breath cycles.
 */
export function startGame() {
  gameStarted = true;
  // Reset the progress baseline so progress maps from the current start position.
  progressBaseRingTopWorldY = getBreathRingTopWorldPosition()?.y ?? null;
  animations.resetMovement();
  animations.resetWinAnimation();
  animations.resetAuraIntensity();
  breathRingLoader.resetBreathRing();
  cloudLoader.resetCloud();
  gameLogic.startGame();
  // eslint-disable-next-line no-console
  console.debug('Scene: game started');
}

/**
 * Pause the game.
 */
export function pauseGame() {
  gameLogic.pauseGame();
  // eslint-disable-next-line no-console
  console.debug('Scene: game paused');
}

/**
 * Resume the game.
 */
export function resumeGame() {
  gameLogic.resumeGame();
  // eslint-disable-next-line no-console
  console.debug('Scene: game resumed');
}

/**
 * Reset the game to initial state.
 */
export function resetGame() {
  gameStarted = false;
  sessionComplete = false;
  sessionFadeProgress = 0;
  progressBaseRingTopWorldY = getBreathRingTopWorldPosition()?.y ?? null;
  animations.resetMovement();
  animations.resetWinAnimation();
  animations.resetAuraIntensity();
  breathRingLoader.resetBreathRing();
  cloudLoader.resetCloud();
  gameLogic.resetGame();
  // eslint-disable-next-line no-console
  console.debug('Scene: game reset');

  // Stop any playing audio when the game is reset.
  stopAndCleanupAudio();
}

/**
 * Immediately trigger the end-game / win sequence.
 * This forces session completion, disables input, and starts the win animation
 * directed to the cloud. It's intended only for debug/testing.
 */
export function triggerEndGameNow() {
  if (sessionComplete) return;

  sessionComplete = true;
  try {
    gameLogic.markSessionComplete();
  } catch (e) {
    void e;
  }

  try {
    // Stop movement and input so nothing fights the win animation
    animations.stopMovement();
    gameLogic.cleanupInputListeners?.();
  } catch (e) {
    void e;
  }

  // mark as not started so UI reflects completed state
  gameStarted = false;

  // Start a win animation pointing at the cloud's Y position (fallback to goal)
  try {
    const cloudState = cloudLoader.getCloudState?.();
    const cloudY =
      cloudState && typeof cloudState.baseY === 'number'
        ? cloudState.baseY
        : gameLogic.getProgressState().goalHeight;
    animations.startWinAnimation(cloudY);
  } catch (e) {
    void e;
  }

  // reset fade so UI will animate out in the loop
  sessionFadeProgress = 0;
  // eslint-disable-next-line no-console
  console.debug(
    'Scene: triggerEndGameNow() invoked — forcing session complete'
  );

  // Play the end audio sequence (ending -> quote). If already playing, restart.
  try {
    stopAndCleanupAudio();
    playEndingThenQuote();
  } catch (e) {
    void e;
  }
}

/**
 * Check if the game has been won.
 */
export function hasGameWon(): boolean {
  return sessionComplete || gameLogic.hasWon();
}

/**
 * Get the current game status.
 */
export function getGameStatus() {
  const viewportProgressPercent = getRingViewportProgressPercent();
  const logicState = gameLogic.getProgressState();
  return {
    ...logicState,
    // Override for UI: show progress based on Buddha reaching the top of the viewport.
    progressPercent: viewportProgressPercent,
    breathingState: gameLogic.getBreathingState(),
    movementState: animations.getMovementState(),
    winAnimationActive: animations.isWinAnimationActive(),
    sessionComplete,
  };
}

// ============================================================================
// TEST SEQUENCE
// ============================================================================
// A temporary test sequence to verify rising/falling movement works correctly.
// This will be removed once input controls are implemented.

/**
 * Run a test sequence that:
 * 1. Rises for 3 seconds
 * 2. Falls for 3 seconds
 * 3. Returns to idle
 *
 * This is for testing purposes only.
 */
export function runMovementTestSequence() {
  if (testSequenceActive) {
    // eslint-disable-next-line no-console
    console.debug('Test sequence already running');
    return;
  }

  testSequenceActive = true;
  // eslint-disable-next-line no-console
  console.debug('Starting movement test sequence...');

  // Reset to starting position first
  animations.resetMovement();

  // Phase 1: Rise for 3 seconds
  animations.startRising();
  // eslint-disable-next-line no-console
  console.debug('Phase 1: Rising for 3 seconds...');

  const t1 = window.setTimeout(() => {
    // Phase 2: Fall for 3 seconds
    animations.startFalling();
    // eslint-disable-next-line no-console
    console.debug('Phase 2: Falling for 3 seconds...');

    const t2 = window.setTimeout(() => {
      // Phase 3: Stop and return to idle
      animations.stopMovement();
      testSequenceActive = false;
      // eslint-disable-next-line no-console
      console.debug('Test sequence complete. Returned to idle.');
    }, 3000);
    testSequenceTimeoutIds.push(t2);
  }, 3000);
  testSequenceTimeoutIds.push(t1);
}

/**
 * Stop the test sequence if running.
 */
export function stopMovementTestSequence() {
  testSequenceTimeoutIds.forEach((id) => window.clearTimeout(id));
  testSequenceTimeoutIds = [];
  testSequenceActive = false;
  animations.stopMovement();
  // eslint-disable-next-line no-console
  console.debug('Test sequence stopped.');
}

export function addDebugHelpers() {
  const rs = resources;
  if (!rs || !rs.scene) return;
  if (debugEnabled) return;
  debugEnabled = true;

  // Axes helper to visualize world axes
  axesHelper = new AxesHelper(1.5);
  rs.scene.add(axesHelper);

  // If character exists, add a bounding box helper around it
  const group = characterHandle?.group as Object3D | null;
  if (group) {
    boxHelper = new BoxHelper(group, 0xffcc00);
    rs.scene.add(boxHelper);
  }
}

export function removeDebugHelpers() {
  const rs2 = resources;
  if (!rs2 || !rs2.scene) return;
  debugEnabled = false;
  try {
    if (axesHelper) {
      rs2.scene.remove(axesHelper);
      axesHelper = null;
    }
  } catch (e) {
    void e;
  }

  try {
    if (boxHelper) {
      rs2.scene.remove(boxHelper);
      boxHelper = null;
    }
  } catch (e) {
    void e;
  }
}

// ============================================================================
// MODEL TRANSFORM CONTROLS
// ============================================================================
// Functions to get and set the Buddha model's position, scale, and rotation
// for real-time fine-tuning via the debug panel.

// Store default values for reset functionality
let defaultModelScale: number | null = null;
let defaultModelPosition: { x: number; y: number; z: number } | null = null;
let defaultModelRotationY: number | null = null;

function captureDefaultTransforms() {
  const group = characterHandle?.group;
  if (!group) return;

  if (defaultModelScale === null) {
    defaultModelScale = group.scale.x; // Uniform scale, so x = y = z
  }
  if (defaultModelPosition === null) {
    defaultModelPosition = {
      x: group.position.x,
      y: group.position.y,
      z: group.position.z,
    };
  }
  if (defaultModelRotationY === null) {
    defaultModelRotationY = group.rotation.y;
  }
}

export function getModelPosition(): { x: number; y: number; z: number } | null {
  const group = characterHandle?.group;
  if (!group) return null;
  captureDefaultTransforms();
  return {
    x: group.position.x,
    y: group.position.y,
    z: group.position.z,
  };
}

export function setModelPosition(x: number, y: number, z: number) {
  const group = characterHandle?.group;
  if (!group) return;
  captureDefaultTransforms();
  group.position.set(x, y, z);
}

export function getModelScale(): number | null {
  const group = characterHandle?.group;
  if (!group) return null;
  captureDefaultTransforms();
  return group.scale.x; // Uniform scale
}

export function setModelScale(scale: number) {
  const group = characterHandle?.group;
  if (!group) return;
  captureDefaultTransforms();
  group.scale.setScalar(scale);
}

export function resetModelScale() {
  const group = characterHandle?.group;
  if (!group || defaultModelScale === null) return;
  group.scale.setScalar(defaultModelScale);
}

export function getModelRotationY(): number | null {
  const group = characterHandle?.group;
  if (!group) return null;
  captureDefaultTransforms();
  // Convert radians to degrees
  return (group.rotation.y * 180) / Math.PI;
}

export function setModelRotationY(degrees: number) {
  const group = characterHandle?.group;
  if (!group) return;
  captureDefaultTransforms();
  // Convert degrees to radians
  group.rotation.y = (degrees * Math.PI) / 180;
}

export function resetModelTransforms() {
  const group = characterHandle?.group;
  if (!group) return;

  if (defaultModelScale !== null) {
    group.scale.setScalar(defaultModelScale);
  }
  if (defaultModelPosition !== null) {
    group.position.set(
      defaultModelPosition.x,
      defaultModelPosition.y,
      defaultModelPosition.z
    );
  }
  if (defaultModelRotationY !== null) {
    group.rotation.y = defaultModelRotationY;
  }
}

// Debug controls for manual position override and freezing movement
export function enableManualBuddhaControl(enabled: boolean) {
  try {
    animations.enableManualPositionOverride(enabled);
  } catch (e) {
    void e;
  }
}

export function setManualBuddhaY(y: number | null) {
  try {
    animations.setManualPositionY(y);
  } catch (e) {
    void e;
  }
}

export function getManualBuddhaY(): number | null {
  try {
    return animations.getManualPositionY();
  } catch (e) {
    return null;
  }
}

export function setFreezeBuddhaMovement(enabled: boolean) {
  try {
    animations.setFreezeMovement(enabled);
  } catch (e) {
    void e;
  }
}

export function isBuddhaMovementFrozen(): boolean {
  try {
    return animations.isMovementFrozen();
  } catch (e) {
    return false;
  }
}

export function getDebugStatus() {
  const movement = animations.getMovementState();
  const pos = getModelPosition();
  const scale = getModelScale();
  const rotY = getModelRotationY();
  const progressState = gameLogic.getProgressState();
  const breathingState = gameLogic.getBreathingState();
  const cloudState = cloudLoader.getCloudState();
  const breathRingState = breathRingLoader.getBreathRingState();
  return {
    videoAttached: false,
    videoLoaded: false,
    videoPlaying: false,
    usingFallback: false,
    videoError: null,
    modelLoaded: characterHandle?.loaded ?? false,
    modelError: characterHandle?.error ?? null,
    modelPosition: pos,
    modelScale: scale,
    modelRotationY: rotY,
    auraCreated: auraMesh !== null,
    breathRingCreated: breathRingMesh !== null,
    breathRingOpacity: breathRingState.opacity.toFixed(2),
    breathRingScale: breathRingState.scale.toFixed(2),
    // breath ring position for debugging
    breathRingPosition: (() => {
      try {
        const p = getRingPosition();
        if (
          !p ||
          typeof p.x !== 'number' ||
          typeof p.y !== 'number' ||
          typeof p.z !== 'number'
        )
          return null;
        return {
          x: p.x.toFixed(2),
          y: p.y.toFixed(2),
          z: p.z.toFixed(2),
        };
      } catch (e) {
        void e;
        return null;
      }
    })(),
    movementMode: movement.mode,
    movementOffset: movement.currentOffset.toFixed(2),
    progressOffset: movement.progressOffset.toFixed(2),
    totalOffset: movement.totalOffset.toFixed(2),
    testSequenceActive: testSequenceActive,
    inputInitialized: gameLogic.isInputInitialized(),
    inputActive: gameLogic.isInputActive(),
    // Cloud state
    cloudLoaded: cloudState.initialized,
    cloudY: cloudState.baseY.toFixed(2),
    cloudGlow: cloudState.glowIntensity.toFixed(2),
    // Game state
    gameStarted: gameStarted,
    gameStatus: progressState.status,
    currentProgress: progressState.currentProgress.toFixed(2),
    goalHeight: progressState.goalHeight.toFixed(2),
    progressPercent: (progressState.progressPercent * 100).toFixed(1) + '%',
    hasWon: progressState.hasWon,
    winAnimationActive: animations.isWinAnimationActive(),
    // Breathing state
    breathingPhase: breathingState.currentPhase,
    breathingPhaseProgress: breathingState.phaseProgress.toFixed(2),
    breathingValidWindow: `${breathingState.validWindowStart.toFixed(2)}→${breathingState.validWindowEnd.toFixed(2)}`,
    breathingValidWindowActive: breathingState.validWindowActive,
    cyclesCompleted: breathingState.cyclesCompleted,
    // Heart breathing removed
  };
}

// Breath ring debug helpers --------------------------------------------------

export function getBreathRingPosition() {
  try {
    const p = getRingPosition();
    if (
      !p ||
      typeof p.x !== 'number' ||
      typeof p.y !== 'number' ||
      typeof p.z !== 'number'
    )
      return null;
    return p;
  } catch (e) {
    void e;
    return null;
  }
}

export function setBreathRingPosition(x: number, y: number, z: number) {
  try {
    setRingPosition?.(x, y, z);
  } catch (e) {
    void e;
  }
}

export function moveBreathRingBy(dx: number, dy: number, dz: number) {
  try {
    moveRingBy?.(dx, dy, dz);
  } catch (e) {
    void e;
  }
}

export function enableBreathRingDebug(enabled: boolean) {
  try {
    enableRingDebug?.(enabled);
  } catch (e) {
    void e;
  }
}

// Heart model and controls removed — not used in the simplified experience

export function disposeScene() {
  // Clean up test sequence timeouts
  stopMovementTestSequence();

  // Clean up input listeners
  gameLogic.cleanupInputListeners();

  // Reset game state
  gameStarted = false;
  // sessionComplete handled separately

  if (loopHandle) {
    loopHandle.stop();
    loopHandle = null;
  }
  if (resources) {
    if (characterHandle) {
      try {
        characterHandle.dispose();
      } catch (e) {
        void e;
      }
      characterHandle = null;
    }
    // Clean up the cloud
    if (cloudHandle) {
      try {
        cloudHandle.dispose();
      } catch (e) {
        void e;
      }
      cloudHandle = null;
    }
    // Clean up the breath ring
    try {
      breathRingLoader.disposeBreathRing();
      breathRingMesh = null;
    } catch (e) {
      void e;
    }
    // Clean up the aura mesh
    if (auraMesh) {
      try {
        if (auraMesh.geometry) auraMesh.geometry.dispose();
        const material = auraMesh.material as MeshBasicMaterial | null;
        if (material) {
          if (material.map) material.map.dispose();
          material.dispose();
        }
      } catch (e) {
        void e;
      }
      auraMesh = null;
    }
    // Clean up the buddhaGroup
    if (buddhaGroup) {
      try {
        resources.scene.remove(buddhaGroup);
      } catch (e) {
        void e;
      }
      buddhaGroup = null;
    }
    // Reset animation state
    animations.resetIdleAnimation();
    animations.resetWinAnimation();
    animations.resetAuraIntensity();
    breathRingLoader.resetBreathRing();
    animationContext = {};

    resources.dispose();
    resources = null;
  }
  // Ensure all audio is stopped and cleaned up when disposing the scene
  stopAndCleanupAudio();
}

export default {
  initScene,
  disposeScene,
  addDebugHelpers,
  removeDebugHelpers,
  getDebugStatus,
  startRising,
  startFalling,
  stopMovement,
  resetMovement,
  runMovementTestSequence,
  stopMovementTestSequence,
  // Game control
  startGame,
  pauseGame,
  resumeGame,
  resetGame,
  hasGameWon,
  getGameStatus,
  // Model transform controls
  getModelPosition,
  setModelPosition,
  getModelScale,
  setModelScale,
  resetModelScale,
  getModelRotationY,
  setModelRotationY,
  resetModelTransforms,
  // Breath ring debug controls
  getBreathRingPosition,
  setBreathRingPosition,
  moveBreathRingBy,
  enableBreathRingDebug,
  // End-game debug helper
  triggerEndGameNow,
  // Debug end-game position overrides
  enableManualBuddhaControl,
  setManualBuddhaY,
  getManualBuddhaY,
  setFreezeBuddhaMovement,
  isBuddhaMovementFrozen,
  // heart removed
};
